import { Component, Input, Output, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import { Quote } from 'src/app/classes/quote';
import { FleetAuthService } from '../fleet-auth.service';
import { FleetAuth, FleetAuthField, FleetAuthFields, FleetAuthLetter, FleetAuthorisation } from 'src/app/models/fleetauth';
import { NotificationService } from 'src/app/shared/notification.service';
import { QuoteCommunication } from 'src/app/models/communication';
import { AuthService } from 'src/app/auth.service';
import { SendReportService } from 'src/app/widgets/send-report/send-report.service';
import { DomSanitizer } from '@angular/platform-browser';
import { resultType } from 'src/app/shared/modal-content/modal-content.component';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { WebsocketsService } from 'src/app/shared/websockets.service';

@Component({
  selector: 'app-salesman-fleet-auth',
  templateUrl: './salesman-fleet-auth.component.html',
  styleUrls: ['./salesman-fleet-auth.component.less']
})
export class SalesmanFleetAuthComponent implements OnInit, OnDestroy {

  @Input() quote: Quote = new Quote();
  @Input() isEditMode: boolean = false;

  @Output() onFleetAuthClose = new EventEmitter<void>();
  @Output() onFleetAuthAuthorise = new EventEmitter<void>();

  private fleetPosUpdateSub: Subscription;

  base64Image: string | ArrayBuffer | null = null;
  base64Doc: string | ArrayBuffer | null = null;

  authData : FleetAuth = new FleetAuth();

  isSaved: boolean = false;
  timeSinceEmailed: string = '';
  isLoading: boolean = false; // Add isLoading state
  isProcessDocumentLoading: boolean = false;

  selectedTab: number = 0; // Default to the first tab
  completedSteps: boolean[] = [true, false, false];

  uploadingFiles: Set<string> = new Set();
  fileValidationErrors: Map<string, string> = new Map();
  maxFileSize : number = 5 * 1024 * 1024; // 5MB
  allowedFileTypes: string[] = ['image/jpeg', 'image/png', 'application/pdf'];

  constructor(public fleetAuthService: FleetAuthService, public notificationService: NotificationService, public authService: AuthService, public sendReportService: SendReportService, public sanitizer: DomSanitizer, private websocketService: WebsocketsService) {
    // Initialize the quote object if needed
    this.quote = new Quote();
  }

  ngOnInit(): void {
    this.isLoading = true; // Set loading state to true

    this.fleetPosUpdateSub = this.websocketService.fleetPosUpdated.subscribe(() => {
      if (this.quote.quoteId) {
        this.updateFleet();
      }
    });
    this.updateFleet();


  }

  updateFleet(){
        this.fleetAuthService.getFleetAuthData(this.quote.quoteId).subscribe({
      next: (data) => {
        this.authData = data;
        for (let field of this.authData.fields) {
            const vehicle = this.quote.customerVehicles.find(
            v => v.Vehicle_Customer_MappingId === this.quote.Vehicle_Customer_MappingId
            );
            if (vehicle && vehicle[field.modelField] !== undefined) {
            field.fleetAuthFieldValue = vehicle[field.modelField];
            }

        if (this.authData.dtEmailed) {
          this.timeSinceEmailed = this.calculateTimeSinceEmailed(this.authData.dtEmailed); 
        }
      }
        this.isLoading = false; 
        this.completedSteps = [this.authData.dtEmailed !== null, this.authData.isAuthorised != null , false];
        this.selectedTab = this.completedSteps.findIndex(step => step === false);
        // if (this.areAllRequiredFieldsFilled()){
        //   this.isSaved = true;
        // }
      },
      error: (error) => {
        this.notificationService.showError('Error fetching fleet auth data:', error);
        this.isLoading = false;
      }
    });
  }


    ngOnDestroy(): void {
    this.fleetPosUpdateSub?.unsubscribe();
  }

  onStepChange(index: number): void {
    if (index <= this.selectedTab || this.completedSteps[index-1]) {
      this.selectedTab = index;
    }
  }

  completeCurrentStep(index: number): void {
    this.completedSteps[index] = true; // Mark the current step as completed
    if (this.selectedTab < this.completedSteps.length - 1) {
      this.selectedTab++; // Move to the next step
    }
  }

  getStepTitle(index: number): string {
    const titles = ['Capture Details', 'Authorisation', 'Auth Outcome'];
    return titles[index];
  }
  
  getStepDescription(index: number): string {
    const descriptions = [
      'Fill in required fields',
      'View/Edit auth status',
      'View Summary'
    ];
    return descriptions[index];
  }
  
  onTabChange(index: number): void {
    this.selectedTab = index; // Sync tab with step
  }
  

  calculateTimeSinceEmailed(dtEmailed: Date): string {
    const now = new Date();
    const emailedDate = new Date(dtEmailed + 'Z');

    const timeDiff = Math.abs(now.getTime() - emailedDate.getTime());
    const diffSeconds = Math.floor(timeDiff / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);


    let timeSinceEmailed = '';
   if (diffMinutes > 0) {
      timeSinceEmailed = `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    }
    else {
      timeSinceEmailed = `${diffSeconds} second${diffSeconds > 1 ? 's' : ''} ago`;
    }

    if (timeSinceEmailed.includes('NaN')) {
      return 'Not emailed yet';
    } else {
    return timeSinceEmailed;
    }
  }

async onFileSelected(event: Event, field: FleetAuthField): Promise<void> {
  
  this.isLoading = true; 
  const files = (event.target as HTMLInputElement).files;
  
  if (!files || files.length === 0) {
    this.notificationService.showError('No file selected.');
    this.isLoading = false; 
    return;
  }

  this.fileValidationErrors.delete(field.fleetAuthFieldName || '');

  const validationResults = this.validateFiles(files, field);
  if (!validationResults.isValid) {
    this.fileValidationErrors.set(field.fleetAuthFieldName || '', validationResults.error);
    this.clearFileInput(event.target as HTMLInputElement);
    this.isLoading = false; 
    return;
  }
  this.uploadingFiles.add(field.fleetAuthFieldName || '');

  try {
    if (!Array.isArray(field.fleetAuthFieldValue)) {
      field.fleetAuthFieldValue = [];
    }

    if (field.dataType === 4 && field.fleetAuthFieldValueId != null){
      await this.loadExistingFieldData(field);
    }

    const processedFiles = await this.processFilesSequentially(files);

    field.fleetAuthFieldValue.push(...processedFiles);
    this.isSaved = false;

    this.notificationService.showSuccess(`${files.length} file(s) uploaded successfully.`);
  } catch (error) {
    this.notificationService.showError('Error uploading files.');
    console.error('Error uploading files:', error);
    this.clearFileInput(event.target as HTMLInputElement);
  } finally {
    this.uploadingFiles.delete(field.fleetAuthFieldName || '');
    this.isLoading = false; 
  }
}

  onLetterUpload(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        this.base64Doc = reader.result;
        if (this.base64Doc && typeof this.base64Doc === 'string') {
          this.authData.authLetterBase64 = this.base64Doc.split(',')[1]; // Extract base64 string
        }
        this.uplaodAuthLetter(); 
      };

      reader.readAsDataURL(file);
      
    }
  }

  downloadFile(source: string, name: string): void {
    if (source) {
      const link = document.createElement('a');
      link.href = 'data:application/pdf;base64,' + source;
      link.download = `${name}.pdf`; 
      link.click();
    } else {
      this.notificationService.showInfo('No file available to download.');
    }
  }

  get hasProcessDocument(): boolean {
    return !!(this.authData.hasFleetProcessDoc || this.authData.fleetProcessDocBase64);
  }

  downloadProcessDocument(): void {
    if (this.isProcessDocumentLoading || !this.hasProcessDocument) {
      return;
    }
    // Retain Download Steps when talking to an API that still returns it inline.
    if (this.authData.fleetProcessDocBase64) {
      this.downloadFile(this.authData.fleetProcessDocBase64, 'Process_Document');
      return;
    }
    this.isProcessDocumentLoading = true;
    this.fleetAuthService.getProcessDocument(this.quote.quoteId).pipe(
      finalize(() => this.isProcessDocumentLoading = false)
    ).subscribe({
      next: (report) => this.downloadFile(report.content, 'Process_Document'),
      error: () => this.notificationService.showError('Failed to download process document.')
    });
  }

  downloadQuote(): void {
        //is it necessary to save the quote?
        let rpt = new QuoteCommunication(this.quote.quoteRef, 
          this.quote.quoteId, 
          this.authService.user.user_id, 
          this.quote.custCell, 
          this.quote.custEmail, 
          this.quote.custId);
        //rpt.disableWhatsapp = this.quote.channelId == 140; //if the buyerId is 140, set the quote not not be whatsappable..
        rpt.disableWhatsapp = false;
        this.sendReportService.assignReport(rpt);
  }
  isImageViewerVisible: boolean = false;
  selectedImages:  string[];
  currentField: FleetAuthField | null = null;


  openFile(quoteId: number, field: FleetAuthField): void {
    this.currentField = field
    this.fleetAuthService.getField(quoteId, field.fleetAuthFieldValueId).subscribe({
      next: (data: FleetAuthField) => {
        this.selectedImages = data.fleetAuthFieldValue;
        this.currentField = data;
        this.isImageViewerVisible = true
      }
      , error: (error) => {
        this.notificationService.showError('Failed to fetch image.');
      }
    });
    ;
  }

  closeImageViewer(): void {
    this.isImageViewerVisible = false;
    this.selectedImages = [];
  }


  saveFleetAuthFields(): void {
    
    this.isLoading = true;
    const fieldsToSave = new FleetAuthFields();
    fieldsToSave.fields = this.authData.fields.filter(field => field.fleetAuthFieldValue !== undefined && field.fleetAuthFieldValue !== null && field.fleetAuthFieldValue.length > 0);
    this.fleetAuthService.saveFleetAuthFields(this.quote.quoteId, fieldsToSave).subscribe({
      next: (data) => {
        this.notificationService.showSuccess('Fleet Auth Fields saved successfully!');
        this.isSaved = true;
        this.authData = data;
        this.isLoading = false; 
      },
      error: (error) => {
        this.notificationService.showError('Failed to save Fleet Auth Fields.');
        this.isLoading = false; 
      }
    });
  }

  private compressFile(file: File, quality: number = 0.5, maxWidth: number = 1024): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scaleFactor = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * scaleFactor;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const compressedDataUrl = canvas.toDataURL(file.type, quality);
            resolve(compressedDataUrl);
          } else {
            reject(new Error('Canvas context not available'));
          }
        };
        img.onerror = (error) => reject(error);
        img.src = event.target?.result as string;
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
  emails = '';
  newEmail = '';

  addEmail(): void {
    const emailList = this.emails
      .split(/[,;]/)
      .map(email => email.trim())
      .filter(email => email);
    if (!emailList.every(email => email.includes('@'))) {
      this.notificationService.showError('One or more emails are invalid.');
      return;
    }

    const existingEmails = this.authData.emailAddresses
      ? this.authData.emailAddresses.split(/[,;]/).map(email => email.trim()).filter(email => email)
      : [];
    const updatedEmails = Array.from(new Set([...existingEmails, ...emailList]));
    this.authData.emailAddresses = updatedEmails.join(', ');

    this.fleetAuthService.updateEmailAddresses(this.quote.quoteId, this.authData).subscribe({
      next: (data) => {

        this.authData = data;
        this.notificationService.showSuccess('Email addresses updated successfully!');
        this.emails = ''; 
      },
      error: (error) => {
        this.notificationService.showError('Failed to update email addresses.');
      }
    });
  }

  public onClickSubmitEAuth(): void {
    this.isLoading = true;
    this.fleetAuthService.saveFleetAuthFields(this.quote.quoteId, this.authData).subscribe({
      next : (data) => {
        this.fleetAuthService.submitFleetEAuth(this.quote.quoteId).subscribe({
          next: (data) => {
            this.notificationService.showSuccess('Auth submitted successfully!');
            this.authData = data;
            this.isLoading = false;
            this.completeCurrentStep(0);
            if (this.authData.isAuthorised === false) {
              this.completeCurrentStep(1);
            }
          },
          error: (error) => {
            this.notificationService.showError('Failed to submit Auth.');
            this.isLoading = false;
          }
        });

      },
      error: (error) => {
        this.notificationService.showError('Failed to save Fleet Auth Fields.');
        this.isLoading = false;
      }
    })
  }

  public onClickCheckEAuthStatus() {
    this.isLoading = true;
    this.fleetAuthService.checkEAuthStatus(this.quote.quoteId).subscribe({
      next: (data) => {
        this.notificationService.ShowAlert({ title: 'Auth Status Checked', message: 'Auth Status: ' + (data.message ? data.message : 'In progress - please check again.'), type: resultType.Information, htmlMessage: ''});
        this.authData = data;
        this.isLoading = false;
        if (this.authData.stateId >= 3) {
          //this is a terribab implementation, but currently works, so needs to be added. @NIC - Refactor this when you can
          this.completeCurrentStep(1);
          this.completeCurrentStep(2);
          
        }
      },
      error: (error) => {
        this.notificationService.showError('Failed to check Auth status.');
        this.isLoading = false;
      }
    });
  }

  public continueQuote() {
    if (this.authData.isAuthorised) {
      this.onFleetAuthAuthorise.emit(); 
    }
    this.onFleetAuthClose.emit();
  }

  public onClickConvertToEmailAuth() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.fleetAuthService.cancelEAuth(this.quote.quoteId).subscribe({
      next: (data) => {
        this.notificationService.showSuccess('Fleet Auth converted to Email Auth successfully!');
        this.authData = data;
        this.completedSteps = [this.authData.dtEmailed !== null, this.authData.isAuthorised != null , false];
        this.selectedTab = this.completedSteps.findIndex(step => step === false);
        this.isLoading = false;
      },
      error: (error) => {
        this.notificationService.showError('Failed to convert Fleet Auth to Email Auth.');
        this.isLoading = false;
      }
    });
  }

  sendEmail(): void {
    this.isLoading = true;
    
    if (!this.isSaved) {
    this.fleetAuthService.saveFleetAuthFields(this.quote.quoteId, this.authData).subscribe({
      next: (data) => {
          this.fleetAuthService.submitFleetAuth(this.quote.quoteId).subscribe({
            next: (data) => {
              this.notificationService.showSuccess('Fleet Auth submitted successfully!');
              this.authData = data; 
              this.isLoading = false; 
              this.completeCurrentStep(0)
            },
            error: (error) => {
              this.notificationService.showError('Failed to submit Fleet Auth.');
              this.isLoading = false;
            }
          });
  },
      error: (error) => {
        this.notificationService.showError('Failed to save Fleet Auth Fields.');
        this.isLoading = false;
      }
    });
    }
    else {
      this.fleetAuthService.submitFleetAuth(this.quote.quoteId).subscribe({
        next: (data) => {
          this.notificationService.showSuccess('Fleet Auth submitted successfully!');
          this.authData = data; 
          this.isLoading = false; 
          this.completeCurrentStep(0)
        },
        error: (error) => {
          this.notificationService.showError('Failed to submit Fleet Auth.');
          this.isLoading = false;
        }
      });
  }
  }

resendEmail(): void {
  this.isLoading = true;

  const emailList = this.newEmail
    .split(/[,;]/)
    .map(email => email.trim())
    .filter(email => email);
  if (!emailList.every(email => email.includes('@'))) {
    this.notificationService.showError('One or more emails are invalid.');
    return;
  }
  
  const updatedEmails = Array.from(new Set([...emailList]));
  this.newEmail = updatedEmails.join(', ');
  this.fleetAuthService.resendEmail(this.quote.quoteId, this.newEmail).subscribe({
    next: (data) => {
      this.notificationService.showSuccess('Fleet Auth email successfully!');
      this.newEmail = '';
    },
    error: (error) => {
      this.notificationService.showError('Failed to send Fleet Auth email.');
      this.isLoading = false;
    }
  });
  
  }

  uplaodAuthLetter(): void {

    let authLetterObj = new FleetAuthLetter;
    authLetterObj.quoteId = this.quote.quoteId;
    authLetterObj.authLetterBase64 = this.authData.authLetterBase64; // Extract base64 string

    this.fleetAuthService.uploadAuthLetterSalesman(authLetterObj).subscribe({
      next: (data) => {
        this.notificationService.showSuccess('Fleet Auth Letter uploaded successfully!');
      }
      , error: (error) => {
        this.notificationService.showError('Failed to upload Fleet Auth Letter.');
      }
    });
  }


  canSubmit(){
    if (this.selectedAuthorisation == true && this.authData.authLetterBase64 != null && this.authData.authNumber != null && this.authData.authNumber != ''){
      return true;

    }
    else if (this.selectedAuthorisation == false && this.authData.message != null && this.authData.message != ''){
      return true;
    }
    return false;
  }

  selectedAuthorisation: boolean | null = null;

  authorise(){
    let authObj = new FleetAuthorisation;
    authObj.isAuthorised = this.selectedAuthorisation ? this.selectedAuthorisation : false// ? 1 : 0;
    authObj.authNumber = this.authData.authNumber;
    if (this.authData.message){
      authObj.message = this.authData.message;
    } else
    {
      authObj.message = '';
    }

    this.fleetAuthService.fleetAuthoriseSalesman(this.quote.quoteId, authObj).subscribe({
      next: (data) => {
        this.notificationService.showSuccess('Fleet Auth submitted successfully!');
        this.authData = data; 
        this.onFleetAuthClose.emit(); 
        if (this.authData.isAuthorised) {
          this.onFleetAuthAuthorise.emit(); 
        }
        this.completeCurrentStep(1);
        this.completeCurrentStep(2);
      }
      , error: (error) => {
        this.notificationService.showError('Failed to submit Fleet Auth.');
      }
    });
  }

  isRefreshing: boolean = false;
  refreshAuthData(): void {
    this.isRefreshing = true;
    this.isLoading = true; // Set loading state to true
    setTimeout(() => {
      this.fleetAuthService.getFleetAuthData(this.quote.quoteId).subscribe({
        next: (data) => {
          this.authData = data;
          if (this.authData.dtEmailed) {
            this.timeSinceEmailed = this.calculateTimeSinceEmailed(this.authData.dtEmailed);
          }
          this.isRefreshing = false;
          this.isLoading = false; // Set loading state to false
          this.notificationService.showSuccess('Fleet Auth data refreshed successfully!');
        },
        error: (error) => {
          this.isRefreshing = false;
          this.isLoading = false; // Set loading state to false
          this.notificationService.showError('Error fetching fleet auth data:', error);
        }
      });
    }, 1000);

  }

  areAllRequiredFieldsFilled(): boolean {
    return this.authData.fields.every(field => {
      if (field.isRequired) {
        return  field.fleetAuthFieldValueId !== null || field.fleetAuthFieldValue !== null;
      }
      return true;
    });

  }

  removeImage(image: string): void {
    this.isLoading = true; 
    if (this.currentField && this.currentField.fleetAuthFieldValue.length > 1) {
      this.currentField.fleetAuthFieldValue.splice(this.currentField.fleetAuthFieldValue.indexOf(image), 1);
      this.authData.fields = this.authData.fields.map(field => {
        if (field.fleetAuthFieldValueId === this.currentField!.fleetAuthFieldValueId) {
          field.fleetAuthFieldValue = this.currentField!.fleetAuthFieldValue;
          return field;
        }
        return field;
      });
      this.isSaved = false;
      this.selectedImages = this.currentField.fleetAuthFieldValue; // Update the displayed images
      this.isLoading = false; 
    } else if (this.currentField) {
      this.fleetAuthService.removeAuthField(this.quote.quoteId, this.currentField.fleetAuthFieldValueId).subscribe({
        next: (data) => {
          this.authData = data
          this.notificationService.showSuccess('Image removed successfully!');
          this.currentField = null;
          this.isLoading = false;
          this.closeImageViewer();
        },
        error: (error) => {
          this.notificationService.showError('Failed to remove image.');
          this.isLoading = false;
        }
      });
    }
    this.isSaved = false;
  }

  openImageInNewTab(imageUrl: string): void {
  const newWindow = window.open();
  if (newWindow) {
    newWindow.document.write(`
      <html>
        <head><title>${this.currentField?.fleetAuthFieldName}</title></head>
        <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #000;">
          <img src="${imageUrl}" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
        </body>
      </html>
    `);
  }
  }

private validateFiles(files: FileList, field: FleetAuthField): {isValid: boolean, error: string} {
  const currentCount = Array.isArray(field.fleetAuthFieldValue) ? field.fleetAuthFieldValue.length : 0;
  const maxFiles = field.dataType === 4 ? 5 : 1; 

  if (currentCount + files.length > maxFiles) {
    return {isValid: false,
       error: `You can upload a maximum of ${maxFiles} files for ${field.fleetAuthFieldName}.`};
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    if (file.size > this.maxFileSize) {
      return {isValid: false, 
              error: `File ${file.name} exceeds the maximum size of 5MB.`};
  }

  if (!this.allowedFileTypes.includes(file.type)) {
    return {isValid: false, 
            error: `File type of ${file.name} is not allowed. Only JPEG, PNG, and PDF are accepted.`};
  }
}
 return {isValid: true, error: ''};
}

private async loadExistingFieldData(field: FleetAuthField): Promise<void> {
  try{
    const data = await this.fleetAuthService.getField(this.quote.quoteId, field.fleetAuthFieldValueId).toPromise();
    if (data && data.fleetAuthFieldValue) {
      field.fleetAuthFieldValue = data.fleetAuthFieldValue;
    } else {
      field.fleetAuthFieldValue = [];
    }
  } catch (error) {
    console.error('Error loading existing field data:', error);
  }
}

private async processFilesSequentially(files: FileList): Promise<string[]> {
  const processedFiles: string[] = [];
  for (let i = 0; i < files.length; i++) {
    try {
      const file = files[i];
      let base64String: string;
      if (file.size > 1 * 1024 * 1024) { // Only compress if file is larger than 1MB 
        base64String = await this.compressFile(file);
      } else {
        base64String = await this.fileToBase64(file);
      }
      processedFiles.push(base64String);
    } catch (error) {
      console.error('Error processing file:', error);
    }
  }
  return processedFiles;
}

private fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      const result = reader.result as string;
      if (result) {
        resolve(result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('File reading failed'));
    };
    
    reader.readAsDataURL(file);
  });
}

private clearFileInput(input: HTMLInputElement): void {
  input.value = '';
}

// Helper method to check if a field is currently uploading
isFieldUploading(fieldName: string): boolean {
  return this.uploadingFiles.has(fieldName);
}

// Helper method to get validation error for a field
getFieldValidationError(fieldName: string): string | undefined {
  return this.fileValidationErrors.get(fieldName);
}

public hasLargeFileFields(): boolean {
  return this.authData.fields?.some(
    f => (f.dataType === 3 || f.dataType === 4) && f.fleetAuthFieldValue?.length > 2
  ) ?? false;
}

isEmailedMoreThanFiveMinutesAgo(dtEmailed: Date): boolean {
  const now = new Date();
  const emailedDate = new Date(dtEmailed + 'Z');

  const timeDiff = Math.abs(now.getTime() - emailedDate.getTime());
  const diffMinutes = timeDiff / (1000 * 60);

  return diffMinutes >= 5;
}
}
